// Le CYCLE DE CHARGEMENT d'un écran — sa donnée et son état tenus au même endroit, pour lui seul.
//
// ⚠️ POURQUOI CE HOOK EXISTE. L'état de chargement était UNE SEULE variable partagée par les onze écrans
// de la console (`dataState`, dans le contexte applicatif), et aucun chargement n'était annulé. Deux
// conséquences, vues à l'usage : une réponse arrivée APRÈS un changement d'écran écrivait sur l'écran
// SUIVANT — qui affichait « Impossible de charger les données » alors qu'il avait parfaitement chargé —
// et un écran sans résultat en posait un autre en « aucun résultat ». Le mécanisme construit pour
// empêcher l'écran de mentir le faisait donc mentir (§23.0), avec le pire des symptômes : un défaut qui
// se répare tout seul au premier « Réessayer », donc impossible à reproduire à volonté.
//
// La correction est STRUCTURELLE et non défensive. Chaque écran possède son état, et une réponse
// abandonnée n'écrit rien. Aucun écran ne peut oublier la garde, puisque aucun écran ne la porte : la
// seule remise à zéro défensive qui existait (Certificats) disparaît avec elle.
import { useCallback, useEffect, useRef, useState } from 'react';

/** Les quatre états §7 d'un écran de données. Le vide et l'échec sont DITS, jamais devinés. */
export type DataState = 'normal' | 'empty' | 'loading' | 'failed';

export interface Resource<T> {
  /** État de CET écran — jamais partagé avec un autre. */
  state: DataState;
  /** Donnée chargée ; `null` tant qu'elle ne l'est pas, et après un échec (rien de périmé n'est gardé). */
  data: T | null;
  /** Relance le chargement. Résout à la fin : les modales l'attendent avant de se refermer. */
  reload: () => Promise<void>;
  /**
   * Le message RÉEL de l'échec (`state === 'failed'`), `null` sinon.
   * ⚠️ Un refus du serveur (403, 500…) n'est pas un service muet : l'écran d'échec le dit tel quel (§23.0).
   */
  error: string | null;
}

/**
 * Charge la donnée d'un écran et en dérive son état d'affichage.
 *
 * @param load    la requête. Reçoit un `AbortSignal` à transmettre à l'API : le chargement est réellement
 *                ANNULÉ, pas seulement ignoré — inutile de faire tourner une requête de 500 lignes pour
 *                un écran que plus personne ne regarde.
 * @param isEmpty ce qui fait qu'un chargement RÉUSSI est vide. Absent = l'écran garde toujours sa
 *                structure (onglets, catalogue de protocoles…) et n'a donc pas d'état vide.
 */
export function useResource<T>(
  load: (signal: AbortSignal) => Promise<T>,
  isEmpty?: (data: T) => boolean,
): Resource<T> {
  // ⚠️ UN SEUL état pour les trois : une issue s'applique d'un geste, sans champ qu'on oublierait de poser.
  const [current, setCurrent] = useState<Outcome<T>>({ state: 'loading', data: null, error: null });

  // ⚠️ Les deux fonctions sont réécrites à chaque rendu (elles sont passées en ligne par les écrans).
  // Les tenir dans des refs est ce qui rend `reload` STABLE : sans cela l'effet de montage se
  // redéclencherait sans fin, et `onRetry` changerait d'identité à chaque rendu.
  const loadRef = useRef(load);
  const isEmptyRef = useRef(isEmpty);
  loadRef.current = load;
  isEmptyRef.current = isEmpty;

  /** Le chargement en cours, s'il y en a un — pour l'annuler quand un autre part ou quand l'écran s'en va. */
  const running = useRef<AbortController | null>(null);

  const reload = useCallback(async () => {
    running.current?.abort(); // une relance annule la précédente : deux réponses, mais une seule vérité
    const ctl = new AbortController();
    running.current = ctl;
    setCurrent((c) => ({ ...c, state: 'loading', error: null }));
    const outcome = await settle(loadRef.current, isEmptyRef.current, ctl.signal);
    if (outcome) setCurrent(outcome);
  }, []);

  useEffect(() => {
    void reload();
    return () => running.current?.abort();
  }, [reload]);

  return { state: current.state, data: current.data, error: current.error, reload };
}

/** L'issue d'un chargement : son état, sa donnée, et le motif d'un échec. */
export interface Outcome<T> {
  state: DataState;
  data: T | null;
  error: string | null;
}

/**
 * L'issue d'UN chargement, ou `null` quand sa réponse n'a plus de destinataire (écran quitté, chargement relancé).
 * ⚠️ C'EST ICI, et à ce seul endroit, que se joue la correction de l'état partagé : une réponse abandonnée ne peut plus
 *    rien affirmer — ni un succès, ni un échec (une annulation volontaire n'est pas une panne de service).
 */
export async function settle<T>(
  load: (signal: AbortSignal) => Promise<T>,
  isEmpty: ((data: T) => boolean) | undefined,
  signal: AbortSignal,
): Promise<Outcome<T> | null> {
  try {
    const loaded = await load(signal);
    if (signal.aborted) return null;
    return { state: isEmpty?.(loaded) ? 'empty' : 'normal', data: loaded, error: null };
  } catch (e) {
    if (signal.aborted) return null;
    return { state: 'failed', data: null, error: failureMessage(e) };
  }
}

/**
 * Le message réel d'un chargement en échec, ou `null` quand l'exception n'en porte aucun.
 * ⚠️ `null` plutôt qu'un texte fabriqué : `String(new Error(''))` rend « Error », et `String({})` rend
 *    « [object Object] » — l'écran afficherait comme motif un mot qui n'en est pas un.
 */
export function failureMessage(e: unknown): string | null {
  const text = e instanceof Error ? e.message : typeof e === 'string' ? e : '';
  return text.trim() || null;
}

/**
 * Rend un `AbortSignal` neuf en PÉRIMANT le précédent.
 *
 * ⚠️ POUR LES CHARGEMENTS SECONDAIRES — le volet de détail attaché à une sélection. Ils gardent leurs
 * propres états d'affichage (inline sous le tableau, pas de `DataGate`) et ne passent donc pas par
 * `useResource` ; il leur manquait pourtant exactement la même garde. Cliquer vite d'une ligne à l'autre
 * pouvait faire atterrir la fiche de la PREMIÈRE sous la sélection de la SECONDE : l'écran affichait alors
 * les liens d'un fournisseur sous le nom d'un autre — plus grave qu'un état de chargement erroné, puisque
 * rien ne signale que ce n'est pas la bonne fiche.
 *
 * L'appelant demande son signal, puis vérifie `signal.aborted` avant d'écrire quoi que ce soit.
 */
export function useFreshSignal(): () => AbortSignal {
  const current = useRef<AbortController | null>(null);
  useEffect(() => () => current.current?.abort(), []); // l'écran part : rien ne doit plus arriver
  return useCallback(() => {
    current.current?.abort();
    current.current = new AbortController();
    return current.current.signal;
  }, []);
}
